import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Bot, GitCompare, Layers, 
  BarChart3, Users, Shield, Key, ScrollText, CreditCard,
  MessageSquare, Settings, ChevronDown, LogOut,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { getDocPermissions, getRoleBadge } from '@/utils/rolePermissions';

// Define navigation with permission requirements
const getNavigationItems = (permissions) => [
  { name: 'Dashboard', href: '/suite/docs', icon: LayoutDashboard, exact: true, show: true },
  { name: 'AI Chat', href: '/suite/docs/chat', icon: Bot, show: true },
  { name: 'Documents', href: '/suite/docs/documents', icon: FileText, show: permissions.canUploadDocuments || permissions.canManageAllDocs },
  { name: 'Compare', href: '/suite/docs/compare', icon: GitCompare, show: permissions.canManageAllDocs },
  { name: 'Batch Analysis', href: '/suite/docs/batch', icon: Layers, show: permissions.canBatchAnalyze },
  { name: 'AI Reports', href: '/suite/docs/reports', icon: BarChart3, show: permissions.canManageAllDocs },
  { name: 'Users', href: '/suite/docs/users', icon: Users, show: permissions.canManageUsers },
  { name: 'Audit Trail', href: '/suite/docs/audit', icon: Shield, show: permissions.canViewAuditLog },
  { name: 'SSO Config', href: '/suite/docs/sso', icon: Key, show: permissions.canConfigureSSO },
  { name: 'Licenses', href: '/suite/docs/licenses', icon: ScrollText, show: permissions.canManageLicenses },
  { name: 'Subscriptions', href: '/suite/docs/subscriptions', icon: CreditCard, show: permissions.canManageLicenses },
  { name: 'Query Management', href: '/suite/docs/queries', icon: MessageSquare, show: permissions.canAnswerQueries },
];

export default function DocsLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const docsUserStr = localStorage.getItem('docsUser');
  const user = docsUserStr ? JSON.parse(docsUserStr) : userData;
  
  // Get role-based permissions
  const permissions = getDocPermissions(user.doc_role || user.role, user.doc_department);
  const navigation = getNavigationItems(permissions).filter(item => item.show);
  const roleBadge = getRoleBadge(user.doc_role || user.role, 'docs');

  const handleLogout = () => {
    localStorage.removeItem('docsUser');
    localStorage.removeItem('docsToken');
    navigate('/portal');
  };

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-slate-900 border-r border-slate-800
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/portal')}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800 mb-3"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            All Products
          </Button>
          <div className="flex items-center gap-3 px-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">MyDoc Orbit</h2>
              <p className="text-xs text-slate-400">Document Intelligence</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navigation.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-purple-600 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium text-sm">
                    {(user.name || user.full_name || 'U')[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.name || user.full_name || 'User'}
                    </p>
                    <Badge className={`${roleBadge.color} text-white text-[10px] px-1.5 py-0`}>
                      {roleBadge.label}
                    </Badge>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 hover:bg-slate-700 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Back to Portal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 overflow-auto bg-slate-950 p-6">
        <Outlet />
      </div>
    </div>
  );
}
